import * as faceapi from "@vladmandic/face-api/dist/face-api.esm.js";
import { Camera, ThumbsUp } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import Webcam from "react-webcam";
import { TutorialBorderIcon } from "../../assets/icons/tutorial-box-icon";
import { TutorialFaceIcon } from "../../assets/icons/tutorial-face-icon";
import { TutorialHairIcon } from "../../assets/icons/tutorial-hair-icon";
import { TutorialUserIcon } from "../../assets/icons/tutorial-user-icon";
import { loadAppSettings } from "../../config/configLoader";
import { isSignedIn } from "../../services/api";
import { getBaseUrlForModels } from "../../utils/BasenameHandler";
import Header from "../Header/Header";
import { faultMessages } from "../Modal/FaultMessagesModal";
import TextCarousel from "../Tutorial/HomeTutorialCarousel";
import { TutorialSlides } from "../Tutorial/TutorialSlides";
import "./Capture.css";
import "./Capture.Responsive.css";

export const resetTutorial = () => {
	localStorage.setItem("hasSeenTutorial", "false");
	window.dispatchEvent(new CustomEvent("resetTutorial"));
};

type postionsProps = "far" | "adjust to center" | "near" | "keep";

interface IFaceRecoginitionsWithPosV2 {
	callback?: (imgSrc: string | undefined) => void;
}

export default function FaceRecoginitionsWithPosV2(props: IFaceRecoginitionsWithPosV2) {
	const [isLoading, setLoading] = useState(true);
	const [hasError] = useState<undefined | string>();
	const [adjustPosition, setadjustPosition] = useState<postionsProps>();
	const [imgSrc, setImgSrc] = useState<string | undefined>(undefined);
	const WebCamRef = useRef<Webcam>(null);
	const [isSending, setIsSending] = useState(false);
	const [isTutorialActive, setIsTutorialActive] = useState(false);
	const [hasSeenTutorial, setHasSeenTutorial] = useState(localStorage.getItem("hasSeenTutorial") === "true");
	const [hasShownAlert, setHasShownAlert] = useState(false);
	const isSigninRef = useRef(false);
	const [config, setConfig] = useState({
		enableAiDebug: false,
	});
	const [deviceOrientation, setDeviceOrientation] = useState<"portrait" | "landscape">(
		window.innerHeight > window.innerWidth ? "portrait" : "landscape"
	);

	useEffect(() => {
		const fetchConfig = async () => {
			try {
				const appSettings = await loadAppSettings();
				setConfig(appSettings);
			} catch (error) {
				faultMessages.showError(error);
			}
		};
		fetchConfig();
	}, []);

	const getAdjustedResolution = (width: number, height: number): [number, number] => {
		let adjustedWidth = width;
		let adjustedHeight = height;

		const isDevicePortrait = (() => {
			if (window.screen && window.screen.orientation) {
				const orientation = window.screen.orientation.type;
				return orientation.includes("portrait");
			}
			return window.innerHeight > window.innerWidth;
		})();

		console.log(`Orientação da câmera: ${isDevicePortrait ? "retrato" : "paisagem"}`);

		if (isDevicePortrait && width > height) {
			[adjustedWidth, adjustedHeight] = [height, width];
			console.log(
				`Invertendo resolução para corresponder à orientação: ${width}x${height} -> ${adjustedWidth}x${adjustedHeight}`
			);
		} else if (!isDevicePortrait && height > width) {
			[adjustedWidth, adjustedHeight] = [height, width];
			console.log(
				`Invertendo resolução para corresponder à orientação: ${width}x${height} -> ${adjustedWidth}x${adjustedHeight}`
			);
		}

		// Aplicar downscale se necessário
		if (adjustedWidth > maxDimension || adjustedHeight > maxDimension) {
			const aspectRatio = adjustedWidth / adjustedHeight;
			if (adjustedWidth > adjustedHeight) {
				adjustedWidth = maxDimension;
				adjustedHeight = Math.round(maxDimension / aspectRatio);
			} else {
				adjustedHeight = maxDimension;
				adjustedWidth = Math.round(maxDimension * aspectRatio);
			}
			console.log(`Aplicando downscale: ${width}x${height} -> ${adjustedWidth}x${adjustedHeight}`);
		}

		return [adjustedWidth, adjustedHeight];
	};

	const maxDimension = 1920;
	const rawCameraWidth = localStorage.getItem("cameraWidth") ? parseInt(localStorage.getItem("cameraWidth")!) : 1280;
	const rawCameraHeight = localStorage.getItem("cameraHeight")
		? parseInt(localStorage.getItem("cameraHeight")!)
		: 720;
	const [cameraWidth, cameraHeight] = getAdjustedResolution(rawCameraWidth, rawCameraHeight);

	const takeScreenshoot = useCallback(() => {
		if (WebCamRef.current) {
			const _img = WebCamRef.current.getScreenshot({
				width: cameraWidth,
				height: cameraHeight,
			});

			setImgSrc(_img as string);
			if (props.callback) props.callback(_img as string);
		}
	}, [WebCamRef, setImgSrc, props, cameraWidth, cameraHeight]);

	const onMediaError = useCallback(async () => {
		try {
			const response = await isSignedIn();

			if (response.isSuccess) {
				isSigninRef.current = true;
			} else {
				isSigninRef.current = false;
			}

			if (!hasShownAlert && isSigninRef.current) {
				setHasShownAlert(true);
				faultMessages.showError(
					"Opa, precisamos da sua câmera",
					"Para seguir em frente, você precisa liberar o acesso à câmera. \n\nComo fazer isso:\n\n1. Clique no ícone à esquerda do link da página (pode ser um cadeado, um 'i' ou outro dependendo do seu navegador).\n2. Encontre a permissão da câmera e selecione 'Permitir'.\n3. Atualize a página e tente de novo.",
					undefined,
					true
				);
			}
		} catch (error) {
			console.error(error);
		}
	}, [hasShownAlert, isSigninRef]);

	const onLikePressed = async () => {
		const img = imgSrc;

		document.querySelector(".popupBackgroundBlur")?.classList.add("opacityControl");
		setIsSending(true);
		try {
			await new Promise((resolve) => setTimeout(resolve, 3000));
			if (img) {
				localStorage.setItem("capturedImage", img);
				const memberIndex = localStorage.getItem("memberIndex");
				window.dispatchEvent(
					new CustomEvent("handleUpdate", {
						detail: { index: memberIndex, newImageUrl: img },
					})
				);
			}
		} finally {
			setIsSending(false);
			document.querySelector(".popupBackgroundBlur")?.classList.remove("opacityControl");
			localStorage.removeItem("takePhotoClicked");
			navigate("/");
		}
	};

	const debugKeyManager = useRef({
		keys: new Set<string>(),
		swipeCount: 0,
		lastSwipeDirection: "",
		touchTimer: null as NodeJS.Timeout | null,
		lastSwipeTime: 0,
		touchStartX: 0,
		touchStartY: 0,
	});

	useEffect(() => {
		const keyManager = debugKeyManager.current;

		const handleKeyDown = (e: KeyboardEvent) => {
			keyManager.keys.add(e.key.toLowerCase());
			if (
				keyManager.keys.has("control") &&
				keyManager.keys.has("alt") &&
				keyManager.keys.has("shift") &&
				keyManager.keys.has("d") &&
				keyManager.keys.has("f")
			) {
				setConfig((prev) => ({ ...prev, enableAiDebug: !prev.enableAiDebug }));
				keyManager.keys.clear();
			}
		};

		const handleKeyUp = (e: KeyboardEvent) => {
			keyManager.keys.delete(e.key.toLowerCase());
		};

		const handleTouchStart = (e: TouchEvent) => {
			keyManager.touchStartX = e.touches[0].clientX;
			keyManager.touchStartY = e.touches[0].clientY;
		};

		const handleTouchEnd = (e: TouchEvent) => {
			const touchEndX = e.changedTouches[0].clientX;
			const diffX = touchEndX - keyManager.touchStartX;

			let direction = "";
			const minSwipeDistance = 50;

			if (Math.abs(diffX) > minSwipeDistance && diffX < 0) {
				direction = "left";
			}

			if (direction === "left") {
				const now = Date.now();

				if (now - keyManager.lastSwipeTime < 300) {
					keyManager.swipeCount++;

					if (keyManager.swipeCount >= 3) {
						setConfig((prev) => ({ ...prev, enableAiDebug: !prev.enableAiDebug }));
						keyManager.swipeCount = 0;
					}
				} else {
					keyManager.swipeCount = 1;
				}

				keyManager.lastSwipeDirection = "left";
				keyManager.lastSwipeTime = now;
				resetSwipeCounter();
			}
		};

		window.addEventListener("keydown", handleKeyDown);
		window.addEventListener("keyup", handleKeyUp);
		window.addEventListener("touchstart", handleTouchStart);
		window.addEventListener("touchend", handleTouchEnd);

		return () => {
			window.removeEventListener("keydown", handleKeyDown);
			window.removeEventListener("keyup", handleKeyUp);
			window.removeEventListener("touchstart", handleTouchStart);
			window.removeEventListener("touchend", handleTouchEnd);
			if (keyManager.touchTimer) {
				clearTimeout(keyManager.touchTimer);
			}
		};
	}, []);

	const resetSwipeCounter = () => {
		if (debugKeyManager.current.touchTimer) {
			clearTimeout(debugKeyManager.current.touchTimer);
		}
		debugKeyManager.current.touchTimer = setTimeout(() => {
			debugKeyManager.current.swipeCount = 0;
		}, 1000);
	};

	// eslint-disable-next-line react-hooks/exhaustive-deps
	const onResetCapturePressed = useCallback(() => {
		setImgSrc(undefined);
		setadjustPosition(undefined);
		setLoading(true);
		setIsSending(false);
		console.log("imgsrc =", imgSrc);
		if (props.callback) props.callback(undefined);
	}, [imgSrc, props]);

	// Valores para validação da posição do rosto com base no dispositivo e orientação
	const isFaceNearDistance = (() => {
		const baseFactor = 0.05;
		const isPortrait = window.innerHeight > window.innerWidth;
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

		if (isIOS && isPortrait) return cameraWidth * (baseFactor * 1.2);
		return cameraWidth * baseFactor;
	})();

	const isFaceFarDistance = (() => {
		const baseFactor = 0.1;
		const isPortrait = window.innerHeight > window.innerWidth;
		const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);

		if (isIOS && isPortrait) return cameraWidth * (baseFactor * 0.8);
		return cameraWidth * baseFactor;
	})();

	const angleTolerance = 2;

	useEffect(() => {
		let trackingProcess: any;
		let isModelsLoaded = false;
		let hasModel: any = false;
		let counting = 0;

		const loadModels = async () => {
			const baseUrlModels = await getBaseUrlForModels();

			await faceapi.nets.tinyFaceDetector.loadFromUri(baseUrlModels);
			await faceapi.nets.faceLandmark68TinyNet.loadFromUri(baseUrlModels);
			await faceapi.nets.faceRecognitionNet.loadFromUri(baseUrlModels);
			await faceapi.nets.ssdMobilenetv1.loadFromUri(baseUrlModels);
		};

		const loadModelsWithCheck = async () => {
			try {
				await loadModels();
				isModelsLoaded = true;
			} catch (e) {
				console.error(e);
				faultMessages.showError(
					"Alguns arquivos desapareceram",
					"Recarregar a página pode nos ajudar a encontrá-los novamente.",
					undefined,
					true
				);
			}
		};

		if (WebCamRef.current) {
			loadModelsWithCheck();

			const trackFace = async (_hasMedia: boolean) => {
				isLoading && setLoading(false);
				if (WebCamRef.current?.video) {
					try {
						const detection: any = await faceapi
							.detectSingleFace(WebCamRef.current?.video, new faceapi.TinyFaceDetectorOptions())
							.withFaceLandmarks(true);

						if (detection) {
							const landmarks = detection.landmarks;
							const angle = detection.angle;

							// Ajuste das coordenadas
							const desiredEyeX = Math.round(cameraWidth * 0.5); // Centro horizontal sempre
							const desiredEyeY = Math.round(
								cameraHeight * (window.innerHeight > window.innerWidth ? 0.5 : 0.45)
							);

							const desiredMouthX = Math.round(cameraWidth * 0.5); // Centro horizontal sempre
							const desiredMouthY = Math.round(
								cameraHeight * (window.innerHeight > window.innerWidth ? 0.7 : 0.65)
							);

							// Ajuste da tolerância baseado na orientação e tipo de dispositivo
							const toleranceMultiplier = (() => {
								const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
								const isPortrait = window.innerHeight > window.innerWidth;

								if (isIOS && isPortrait) return 0.08;
								if (isIOS) return 0.06;
								if (isPortrait) return 0.07;
								return 0.05;
							})();

							const tolerance = Math.min(cameraWidth, cameraHeight) * toleranceMultiplier;

							// Obter posições atuais dos olhos e boca
							const leftEye = landmarks.getLeftEye();
							const rightEye = landmarks.getRightEye();
							const mouth = landmarks.getMouth();

							// Calcula o centro dos olhos e da boca dentro da área de captura.
							// Esse cálculo cria "cordenadas", demarcando onde os olhos e a boca devem ficar.
							const eyesCenterX = (leftEye[0].x + rightEye[3].x) / 2;
							const eyesCenterY = (leftEye[0].y + rightEye[3].y) / 2;
							const mouthCenterX = (mouth[0].x + mouth[6].x) / 2;
							const mouthCenterY = (mouth[0].y + mouth[6].y) / 2;

							// Verifica se os olhos estão dentro da cordenada e na margem de tolerância
							const eyesWithinTolerance =
								Math.abs(eyesCenterX - desiredEyeX) < tolerance &&
								Math.abs(eyesCenterY - desiredEyeY) < tolerance;

							// Verifica se a boca está dentro da cordenada e na margem de tolerância
							const mouthWithinTolerance =
								Math.abs(mouthCenterX - desiredMouthX) < tolerance &&
								Math.abs(mouthCenterY - desiredMouthY) < tolerance;

							// Calcula a distância entre os olhos, essencial para definir o quão perto/longe um usuário deve ficar de sua câmera
							const distanceBetweenEyes = Math.sqrt(
								Math.pow(rightEye[0]._x - leftEye[3]._x, 2) +
									Math.pow(rightEye[0]._y - leftEye[3]._y, 2)
							);

							// Verificar se o ângulo do rosto está dentro da margem de tolerância
							const angleWithTolerance =
								angle.row > -angleTolerance &&
								angle.row < angleTolerance &&
								angle.pitch > -angleTolerance &&
								angle.pitch < angleTolerance &&
								angle.yaw > -angleTolerance &&
								angle.yaw < angleTolerance;

							// Debug overlay code
							if (config.enableAiDebug) {
								const debugInfo = document.createElement("div");
								debugInfo.className = "face-debug-overlay";
								debugInfo.innerHTML = `
								<div style="position:fixed; top:50%; left:50%; transform:translate(-50%, -50%); z-index:9999; 
									background:rgba(0,0,0,0.8); color:white; padding:15px; border-radius:5px; 
									width:300px; font-family:monospace; font-size:12px; text-align:left;">
									<div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:10px;">
										<h3 style="margin:0; text-align:center; color: orange;">Face Detection Debug</h3>
										<button id="closeDebug" style="background:#f44336; color:white; border:none; border-radius:3px; padding:3px 8px; cursor:pointer;">X</button>
									</div>
									<div style="margin-bottom:8px;">
										<b>Eyes X:</b> ${Math.round(eyesCenterX)} / ${desiredEyeX} (±${tolerance})
										<span style="color:${Math.abs(eyesCenterX - desiredEyeX) < tolerance ? "green" : "red"}">
											${Math.abs(eyesCenterX - desiredEyeX) < tolerance ? "✓ IDEAL" : "✗ NOT IDEAL"}
										</span>
									</div>
									<div style="margin-bottom:8px;">
										<b>Eyes Y:</b> ${Math.round(eyesCenterY)} / ${desiredEyeY} (±${tolerance})
										<span style="color:${Math.abs(eyesCenterY - desiredEyeY) < tolerance ? "green" : "red"}">
											${Math.abs(eyesCenterY - desiredEyeY) < tolerance ? "✓ IDEAL" : "✗ NOT IDEAL"}
										</span>
									</div>
									<div style="margin-bottom:8px;">
										<b>Mouth X:</b> ${Math.round(mouthCenterX)} / ${desiredMouthX} (±${tolerance})
										<span style="color:${Math.abs(mouthCenterX - desiredMouthX) < tolerance ? "green" : "red"}">
											${Math.abs(mouthCenterX - desiredMouthX) < tolerance ? "✓ IDEAL" : "✗ NOT IDEAL"}
										</span>
									</div>
									<div style="margin-bottom:8px;">
										<b>Mouth Y:</b> ${Math.round(mouthCenterY)} / ${desiredMouthY} (±${tolerance})
										<span style="color:${Math.abs(mouthCenterY - desiredMouthY) < tolerance ? "green" : "red"}">
											${Math.abs(mouthCenterY - desiredMouthY) < tolerance ? "✓ IDEAL" : "✗ NOT IDEAL"}
										</span>
									</div>
									<div style="margin-bottom:8px;">
										<b>Distance Between Eyes:</b> ${Math.round(distanceBetweenEyes)} 
										<span style="color:${
											distanceBetweenEyes > isFaceNearDistance &&
											distanceBetweenEyes < isFaceFarDistance
												? "green"
												: "red"
										}">
											${distanceBetweenEyes > isFaceNearDistance && distanceBetweenEyes < isFaceFarDistance ? "✓ IDEAL" : "✗ NOT IDEAL"}
										</span>
										(${isFaceFarDistance}-${isFaceNearDistance} ideal)
									</div>
									<hr style="margin:10px 0; border-color:#444;">
									<div style="margin-bottom:8px;"> 
										<b>Resolution captured:</b> ${rawCameraWidth}x${rawCameraHeight} 
									</div>
									<div style="margin-bottom:8px;">
										<b>Need downscale:</b> 
										<span style="color:${
											rawCameraWidth > maxDimension || rawCameraHeight > maxDimension
												? "green"
												: "red"
										}; font-weight:bold;">
											${rawCameraWidth > maxDimension || rawCameraHeight > maxDimension ? "TRUE" : "FALSE"}
										</span>
									</div>
									${
										rawCameraWidth > maxDimension || rawCameraHeight > maxDimension
											? `
									<div style="margin-bottom:8px;">
										<b>Adjusted resolution:</b> ${cameraWidth}x${cameraHeight}
									</div>
									`
											: ""
									}
									<hr style="margin:10px 0; border-color:#444;">
									<div style="display:flex; justify-content:space-between;">
										<div>
											<b>Eyes:</b> 
											<span style="color:${eyesWithinTolerance ? "green" : "red"}; font-weight:bold;">
												${eyesWithinTolerance ? "✓" : "✗"}
											</span>
										</div>
										<div>
											<b>Mouth:</b> 
											<span style="color:${mouthWithinTolerance ? "green" : "red"}; font-weight:bold;">
												${mouthWithinTolerance ? "✓" : "✗"}
											</span>
										</div>
										<div>
											<b>Distance:</b> 
											<span style="color:${
												distanceBetweenEyes > isFaceNearDistance &&
												distanceBetweenEyes < isFaceFarDistance
													? "green"
													: "red"
											}; font-weight:bold;">
												${distanceBetweenEyes > isFaceNearDistance && distanceBetweenEyes < isFaceFarDistance ? "✓" : "✗"}
											</span>
										</div>
									</div>
									<div style="margin-top:10px; font-size:10px; text-align:center; color:#aaa;">
										Para fechar, deixe a imagem/câmera estável e aperte o "X"
									</div>
								</div>
								`;

								const existingDebug = document.querySelector(".face-debug-overlay");
								if (existingDebug) {
									existingDebug.remove();
								}

								document.body.appendChild(debugInfo);

								document.getElementById("closeDebug")?.addEventListener("click", () => {
									const debugOverlay = document.querySelector(".face-debug-overlay");
									if (debugOverlay) debugOverlay.remove();
									setConfig((prev) => ({ ...prev, enableAiDebug: false }));
								});
							}

							return [eyesWithinTolerance, mouthWithinTolerance, distanceBetweenEyes, angleWithTolerance];
						}
					} catch (e) {
						console.error(e);
						faultMessages.showError(
							"Arquivo indecifrável",
							"Alguns arquivos esqueceram como se comunicar com nosso site. Recarregar a página pode faze-los lembrar.",
							undefined,
							true
						);
					}
				}
			};

			// pos[0] (eyesWithinTolerance): Um booleano que indica se os olhos estão dentro da margem de tolerância definida
			// Verifica se o centro dos olhos está próximo às coordenadas desejadas (desiredEyeX e desiredEyeY)

			// pos[1] (mouthWithinTolerance): Um booleano que indica se a boca está dentro da margem de tolerância definida
			// Verifica se o centro da boca está próximo às coordenadas desejadas (desiredMouthX e desiredMouthY)

			// pos[2] (distanceBetweenEyes): Um número que representa a distância entre os olhos
			// Usado para determinar se o rosto está muito próximo (near) ou muito longe (far) da câmera
			// É comparado com as constantes isFaceNearDistance e isFaceFarDistance

			// pos[3] (angleWithTolerance): Um booleano que indica se o ângulo do rosto está dentro da tolerância aceitável
			// Verifica se o rosto não está muito inclinado em nenhuma direção (pitch, yaw, roll)

			const validateFace = async (pos: any) => {
				if (pos[0] && pos[1] && pos[2] > isFaceNearDistance && pos[2] < isFaceFarDistance && angleTolerance) {
					setadjustPosition("keep");
					return true;
				} else {
					if (pos[2] > isFaceNearDistance) setadjustPosition("near");
					if (pos[2] < isFaceFarDistance) setadjustPosition("far");
					if (pos[2] > isFaceNearDistance && pos[2] < isFaceFarDistance)
						setadjustPosition("adjust to center");
					return false;
				}
			};

			const startService = async () => {
				trackingProcess = setInterval(async () => {
					if (isModelsLoaded) {
						if (WebCamRef.current?.video?.readyState === 4) {
							const result = await trackFace(true);
							if (result) {
								hasModel = await validateFace(result);
								if (hasModel) {
									counting = counting + 1;
									console.log(counting);
									if (counting > 5) {
										clearInterval(trackingProcess);
										takeScreenshoot();
									}
								}
							}
						}
					}
					console.log("Tracking...");
				}, 500);
			};

			const Reset = async () => {
				clearInterval(trackingProcess);
				setHasShownAlert(false);
				window.location.reload();
			};

			document.addEventListener("visibilitychange", Reset);

			startService();

			return () => {
				clearInterval(trackingProcess);
				document.removeEventListener("visibilitychange", Reset);
			};
		}
	}, [
		isLoading,
		onMediaError,
		isFaceNearDistance,
		isFaceFarDistance,
		cameraWidth,
		cameraHeight,
		takeScreenshoot,
		rawCameraWidth,
		rawCameraHeight,
		config.enableAiDebug,
	]);

	useEffect(() => {
		// Função para detectar mudanças de orientação
		const handleOrientationChange = () => {
			const newOrientation = window.innerHeight > window.innerWidth ? "portrait" : "landscape";

			if (newOrientation !== deviceOrientation) {
				console.log(`Orientação alterada: ${deviceOrientation} -> ${newOrientation}`);
				setDeviceOrientation(newOrientation);

				// Se estiver tentando capturar uma foto, resete o processo
				if (!imgSrc && !isLoading) {
					console.log("Reiniciando captura após mudança de orientação");
					onResetCapturePressed();
				}
			}
		};

		// Detectar mudanças de orientação
		window.addEventListener("resize", handleOrientationChange);

		if (window.screen?.orientation) {
			window.screen.orientation.addEventListener("change", handleOrientationChange);
		}

		return () => {
			window.removeEventListener("resize", handleOrientationChange);
			if (window.screen?.orientation) {
				window.screen.orientation.removeEventListener("change", handleOrientationChange);
			}
		};
	}, [deviceOrientation, imgSrc, isLoading, onResetCapturePressed]);

	useEffect(() => {
		if (hasSeenTutorial) {
			setIsTutorialActive(false);
		}
	}, [hasSeenTutorial]);

	const handleTutorialComplete = () => {
		setIsTutorialActive(false);
		setHasSeenTutorial(true);
		localStorage.setItem("hasSeenTutorial", "true");
	};

	const navigate = useNavigate();
	useEffect(() => {
		const takePhotoClicked = localStorage.getItem("takePhotoClicked");
		if (!takePhotoClicked) {
			navigate("/"); //homepage
		}
	}, [navigate]);

	return (
		<div>
			<Header ShowSignOutHeader={true} />
			<div className="HomeBody">
				<div className="popupBackgroundBlur">
					{isSending && (
						<div className="popupWrapper">
							<div className="recoginitionPopup">
								<span className="loaderIcon"></span>
								<div className="sendingMessage">
									<h5>Carregando sua foto...</h5>
								</div>
							</div>
						</div>
					)}
				</div>
				<div className="homeTutorial">
					<div className="homeTutorialWrapper">
						<div className="homeTutorialFace">
							<TutorialFaceIcon />
							<TutorialUserIcon />
							<TutorialHairIcon />
							<TutorialBorderIcon />
						</div>
						<TextCarousel slides={TutorialSlides} onComplete={handleTutorialComplete} />
					</div>
				</div>
				<div className="homeTutorialButton">
					<button
						onClick={() => {
							setIsTutorialActive(!isTutorialActive);
							resetTutorial();
						}}
					>
						<svg className="tutorialIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
							<path d="M12 4C9.23772 4 7 6.23772 7 9C7 9.55228 7.44772 10 8 10C8.55228 10 9 9.55228 9 9C9 7.34228 10.3423 6 12 6C13.6577 6 15 7.34228 15 9C15 9.81648 14.8014 10.2945 14.5621 10.6286C14.3004 10.9938 13.9373 11.2671 13.4335 11.6135L13.3183 11.6923C12.8712 11.9977 12.2953 12.391 11.8493 12.9392C11.3224 13.5869 11 14.4061 11 15.5V16C11 16.5523 11.4477 17 12 17C12.5523 17 13 16.5523 13 16V15.5C13 14.8439 13.1776 14.4756 13.4007 14.2014C13.6567 13.8868 14.0026 13.6491 14.5137 13.2978L14.5665 13.2615C15.0627 12.9204 15.6996 12.4749 16.1879 11.7933C16.6986 11.0805 17 10.1835 17 9C17 6.23772 14.7623 4 12 4ZM12 21.25C12.6904 21.25 13.25 20.6904 13.25 20C13.25 19.3096 12.6904 18.75 12 18.75C11.3096 18.75 10.75 19.3096 10.75 20C10.75 20.6904 11.3096 21.25 12 21.25Z" />
						</svg>
					</button>
				</div>
				<div className="captureBody">
					<div
						className="outerContainer"
						style={{
							borderBottomLeftRadius: isLoading ? undefined : "0",
							borderBottomRightRadius: isLoading ? undefined : "0",
						}}
					>
						<div className="innerContainer">
							{isLoading && <div className="spinnerContainer"></div>}
							<div className="faceLandmarksContainer">
								{!isLoading && !hasError && !imgSrc && adjustPosition && (
									<div
										className="bound"
										style={{
											borderColor:
												adjustPosition === "keep"
													? "var(--capture-bound-color-good)"
													: "var(--capture-bound-color-bad)",
										}}
									/>
								)}
							</div>
							<div className="webcamContainer">
								{hasError && (
									<div className="errorText">
										{hasError}
										<button onClick={() => window.location.reload()}>Recarregar</button>
									</div>
								)}
								{imgSrc ? (
									<img src={imgSrc} alt="print" className="imageSrc" />
								) : (
									!isTutorialActive && (
										<Webcam
											autoPlay
											audio={false}
											ref={WebCamRef}
											style={{
												objectFit: "cover",
												width: "100%",
												height: "100%",
											}}
											videoConstraints={{
												facingMode: "user",
												width: cameraWidth,
												height: cameraHeight,
											}}
											mirrored
											onUserMediaError={onMediaError}
										/>
									)
								)}
							</div>
						</div>
					</div>
					{!isLoading && !imgSrc && (
						<div className="helpTextContainer">
							{adjustPosition === "far" && <> Aproxime-se </>}
							{adjustPosition === "near" && <> Afaste-se </>}
							{adjustPosition === "adjust to center" && <>Centralize o rosto</>}
							{adjustPosition === "keep" && (
								<div style={{ color: "var(--capture-bound-color-good)" }}>Quase lá...</div>
							)}
							{adjustPosition === undefined && (
								<div>
									<p className="adjustPositionText">Posicione seu rosto na tela</p>
								</div>
							)}
						</div>
					)}
					{imgSrc && (
						<div className="buttonsContainer">
							<button
								className="btn buttonOk"
								onClick={(e) => {
									e.preventDefault();
									onLikePressed();
								}}
							>
								Salvar
								<ThumbsUp />
							</button>
							<button
								className="btn buttonRetake"
								onClick={(e) => {
									e.preventDefault();
									onResetCapturePressed();
								}}
							>
								Tirar outra
								<Camera />
							</button>
						</div>
					)}
				</div>
				<Link to="/" className="linkToHome">
					<svg className="arrowIcon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
						<path d="M15.7071 4.29289C16.0976 4.68342 16.0976 5.31658 15.7071 5.70711L9.41421 12L15.7071 18.2929C16.0976 18.6834 16.0976 19.3166 15.7071 19.7071C15.3166 20.0976 14.6834 20.0976 14.2929 19.7071L7.29289 12.7071C6.90237 12.3166 6.90237 11.6834 7.29289 11.2929L14.2929 4.29289C14.6834 3.90237 15.3166 3.90237 15.7071 4.29289Z" />
					</svg>
					<div>Cancelar</div>
				</Link>
			</div>
		</div>
	);
}
